import { useState } from "react";
import classNames from "classnames";
import report from "@report";
import { AccountDrawer } from "@layouts/PageSider/AccountDrawer";
import { goPageIndex } from "@routes";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconCredit,
  IconToggle,
  IconCountDown,
  IconLogo3,
  IconLogoShort3,
  IconVideo,
  IconScript,
  IconIdea,
  IconVideoFull,
  IconScriptFull,
  IconIdeaFull,
  IconSocialPublisher,
  IconSocialPublisherFull,
  IconFolder,
  IconFolderColour,
  IconFolderFull,
  IconLoading,
  IconVideoRemake,
  IconVideoRemakeActive,
} from "@icons/index";
import { useAppSelector } from "@hooks/redux";
import { useIsCreditActivityEligible } from "@store/globalSlice";
import "./index.scss";
import { useGasStation } from "@pages/pricing/hooks";
import { CreditConfig } from "@layouts/ShoppingCart/config";
import { Trans } from "react-i18next";
import { Badge, Popover, Progress, Tag } from "antd";
import { statusConfig } from "@pages/library/librarySlice";
import { useLibraryPolling } from "./hooks";
import { useEffect } from "react";
import { useVideoGenerationNotification } from "@hooks/useVideoGenerationNotification";
import { RotatingGradientBg } from "@pages/library/UICardWrap/UIVideoGenerateCard";
import TruncateTitle from "@components/TruncateTitle";
import { CommonButton } from "@components/CommonButton";
import { themeAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";
import { isNil } from "@kalodata/tools";
import { pushGtmEvent, pushGtmSidebarNavigation } from "@utils/gtm";
import { GtmEvent, type GtmSidebarNavigationEvent } from "@utils/gtmEvents";
import { isDevelopmentOrStaging } from "@utils/env";
import { TestId } from "@shared/testIds";

export type HeaderMenuItem = { name: string; link: string };
export type HeaderMenu = {
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
  pathList: string[];
  name: string;
  link: string;
  testId: TestId;
  gtmEvent: GtmSidebarNavigationEvent;
  hoverBadgeLabel?: string;
};

const getTimeAgo = (dateStr?: string | number) => {
  if (!dateStr) return "";
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}S ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}M ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}H ago`;
};

export const PageSider = () => {
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const { addGasStationToShoppingCart } = useGasStation();
  const { resetPolling, processingTasks, items: generatedItems } = useLibraryPolling();
  const isDark = useAtomValue(themeAtom) === "dark";
  const { isCreditActivityEligible } = useIsCreditActivityEligible();

  // 监听视频生成完成并发送通知（仅在查看生成列表时启用）
  useVideoGenerationNotification(generatedItems);

  const location = useLocation();

  const toMs = (value?: string | number) => {
    if (isNil(value)) return undefined;
    return typeof value === "number" ? value : new Date(value).getTime();
  };

  // 每秒更新的当前时间戳，用于倒计时和进度计算
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (processingTasks.length === 0) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [processingTasks.length]);

  /** 剩余时间倒计时文案 */
  const getCountdown = (estimatedValue?: string | number) => {
    const endMs = toMs(estimatedValue);
    if (endMs === undefined) return "--";
    const left = Math.max(0, Math.floor((endMs - now) / 1000));
    if (left <= 0) return "Almost done";
    const h = Math.floor(left / 3600);
    const m = Math.floor((left % 3600) / 60);
    const s = left % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  /** 根据 gmtCreated ~ gmtEstimatedCompleted 计算进度百分比 */
  const getProgress = (createdValue?: string | number, estimatedValue?: string | number) => {
    const start = toMs(createdValue);
    const end = toMs(estimatedValue);
    if (start === undefined || end === undefined) return 0;
    const total = end - start;
    if (total <= 0) return 95;
    const elapsed = now - start;
    return Math.min(95, Math.max(0, Math.round((elapsed / total) * 100)));
  };

  const menus: HeaderMenu[] = [
    {
      icon: <IconVideo className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      activeIcon: <IconVideoFull className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      pathList: ["/video", "/video-creating"],
      name: "AI Video Creator",
      link: "/video",
      testId: TestId.SidebarNavVideo,
      gtmEvent: GtmEvent.GoToVideo,
    },
    isDevelopmentOrStaging() && {
      icon: <IconVideoRemake className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      activeIcon: <IconVideoRemakeActive className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      pathList: ["/video-remake", "/video-remake/welcome", "/video-remake/studio"],
      name: "Video Remake",
      link: "/video-remake",
      testId: TestId.SidebarNavVideoRemake,
      gtmEvent: GtmEvent.GoToVideoRemake,
    },
    {
      icon: <IconScript className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      activeIcon: <IconScriptFull className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      pathList: ["/insight"],
      name: "Video to Storyboard",
      link: "/insight",
      testId: TestId.SidebarNavInsight,
      gtmEvent: GtmEvent.GoToInsight,
    },
    {
      icon: <IconIdea className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      activeIcon: <IconIdeaFull className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      pathList: ["/script-rewrite"],
      name: "AI Script Writer",
      link: "/script-rewrite",
      testId: TestId.SidebarNavScriptRewrite,
      gtmEvent: GtmEvent.GoToScriptRewrite,
    },
    {
      icon: <IconSocialPublisher className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      activeIcon: <IconSocialPublisherFull className={classNames("shrink-0 w-[20px] h-[20px]")} />,
      pathList: ["/social-publisher"],
      name: "Social Publisher",
      link: "/social-publisher",
      testId: TestId.SidebarNavSocialPublisher,
      gtmEvent: GtmEvent.GoToSocialPublisher,
      hoverBadgeLabel: "Earn Credits",
    },
    // {
    //   icon: <IconMenuPic className={classNames("shrink-0 w-[20px] h-[20px]")} />,
    //   name: "AI Photo Studio",
    //   shotName: "Image",
    //   link: "/image",
    // },
  ].filter(Boolean) as HeaderMenu[];

  const libraryPopoverContent = (
    <div className="border-kds-border w-[420px] max-h-[300px] overflow-y-auto scrollbar max-h-500px">
      <div className="flex flex-col gap-[12px] p-[12px]">
        {processingTasks.length > 0 ? (
          processingTasks.map((task) => {
            const status = task?.jobItem?.status;
            const config = (statusConfig as any)[status || "queued"];
            return (
              <div key={task.id} className="flex items-center gap-[12px] bg-kds-bg-card generate-card-item">
                <div className="w-[72px] h-[72px] rounded-[4px] bg-kds-bg-soft relative overflow-hidden flex items-center justify-center">
                  <RotatingGradientBg showDot={false} />
                  <IconLoading className="w-[16px] h-[16px] animate-spin opacity-50" />
                </div>

                <div className="flex-1 min-w-0 flex flex-col justify-between h-[72px]">
                  <div className="flex items-center gap-[8px] mb-[4px]">
                    <Tag color={config?.color} className="rounded-full!">
                      <Trans>{config?.label}</Trans>
                    </Tag>
                    <span className="text-[14px] font-semibold text-kds-fg truncate">
                      <Trans>Video Generation</Trans>
                    </span>
                  </div>
                  <div className="text-[13px] text-kds-fg-subtle truncate">{getTimeAgo(task.gmtCreated)}</div>

                  {/* Progress Bar */}
                  {status === "processing" && (
                    <div className="flex items-center gap-[8px]">
                      <Progress
                        percent={getProgress(task.gmtCreated, task.gmtEstimatedCompleted)}
                        showInfo={false}
                        strokeColor={isDark ? { from: "#71717A", to: "white" } : { from: "#0191F8", to: "#8466FD" }}
                        className={classNames("flex-1", !isDark && "task-progress")}
                      />
                      <div className="flex items-center gap-[4px] text-kds-fg-subtle shrink-0">
                        <IconCountDown className="w-[16px] h-[16px]" />
                        <span className="text-[12px]">{getCountdown(task.gmtEstimatedCompleted)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-[40px]">
            <div className="text-[14px] text-kds-fg-subtle">
              <Trans>All caught up! Keep 'em coming!</Trans>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const libraryContent = (
    <Badge count={processingTasks.length} offset={[1, 0]} size="small" className="w-full!">
      <div
        className={classNames(
          "flex items-center gap-[8px] cursor-pointer p-[6px] rounded-[8px] menu-item",
          ["/library"].includes(location.pathname) ? "active" : "",
          isOpen ? "" : "flex-col w-[32px] m-auto",
        )}
        data-testid={TestId.SidebarNavLibrary}
        onClick={() => {
          pushGtmSidebarNavigation({
            event: GtmEvent.GoToLibrary,
            destination: "/library",
            from_path: location.pathname,
          });
          navigate("/library");
        }}
      >
        {processingTasks.length > 0 ? (
          <IconFolderColour className="w-[20px] h-[20px] shrink-0" />
        ) : (
          <>
            {["/library"].includes(location.pathname) ? (
              <IconFolderFull className="w-[20px] h-[20px] shrink-0" />
            ) : (
              <IconFolder className="w-[20px] h-[20px] shrink-0" />
            )}
          </>
        )}
        {isOpen && (
          <span className="text-kds-fg-soft whitespace-nowrap">
            <Trans>Library</Trans>
          </span>
        )}
      </div>
    </Badge>
  );

  return (
    <div
      className={classNames(
        "min-h-screen relative flex flex-col justify-between bg-kds-bg-soft z-10 border-r border-kds-border transition-[width] duration-300 shrink-0 group",
        isOpen ? "w-[220px]" : "w-[60px]",
      )}
    >
      <div className="px-[12px] h-[53px] border-b border-kds-border relative flex items-center justify-between overflow-x-hidden group/logo">
        {isOpen ? (
          <IconLogo3 className="w-[103px] h-[24px] cursor-pointer shrink-0" onClick={goPageIndex} />
        ) : (
          <div className="relative flex items-center justify-center w-full">
            <IconLogoShort3
              className="h-[24px] w-[24px] cursor-pointer group-hover/logo:opacity-0 transition-opacity"
              onClick={goPageIndex}
            />
            <div
              className="absolute -top-[4px] inset-0 flex items-center justify-center w-[36px] h-[36px] flex-center opacity-0 group-hover/logo:opacity-100 transition-opacity cursor-pointer hover:bg-kds-bg-overlay rounded-[4px]"
              onClick={() => setIsOpen(true)}
            >
              <IconToggle className="w-[20px] h-[20px]" />
            </div>
          </div>
        )}
      </div>
      {isOpen && (
        <div
          className="absolute top-[8px] right-[8px] cursor-pointer w-[36px] h-[36px] flex-center hover:bg-kds-bg-overlay rounded-[4px]"
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <IconToggle className="w-[20px] h-[20px]" />
        </div>
      )}
      <div
        className={classNames(
          "flex-1 flex flex-col gap-[8px] overflow-hidden",
          isOpen ? "p-[12px]" : "px-[8px] py-[12px] items-center",
        )}
      >
        {menus.map((menuItem) => {
          const isActive = menuItem.pathList.includes(location.pathname);
          const showBadge = isOpen && isCreditActivityEligible && Boolean(menuItem.hoverBadgeLabel);
          return (
            <div
              className={classNames(
                "group/menu relative flex items-center cursor-pointer px-[8px] py-[6px] rounded-[8px] menu-item text-kds-fg-soft",
                isActive ? "active" : "",
                isOpen ? "gap-[8px]" : "flex-col w-[32px]",
              )}
              data-testid={menuItem.testId}
              key={menuItem.name}
              onClick={() => {
                if (menuItem.gtmEvent) {
                  pushGtmSidebarNavigation({
                    event: menuItem.gtmEvent,
                    destination: menuItem.link,
                    from_path: location.pathname,
                  });
                }
                navigate(menuItem.link);
              }}
            >
              {isActive ? menuItem.activeIcon : menuItem.icon}
              {isOpen && <TruncateTitle text={menuItem.name} className="flex-1 min-w-0" />}
              {showBadge && (
                <span
                  className={classNames(
                    "pointer-events-none absolute -top-[6px] right-[8px] z-10",
                    "rounded-full bg-kds-danger px-[4px] py-px",
                    "text-[10px] font-semibold leading-[12px] text-kds-light",
                  )}
                >
                  <Trans>{menuItem.hoverBadgeLabel}</Trans>
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="border-t border-kds-border py-[12px] px-[8px] flex flex-col gap-[8px]">
        {processingTasks.length > 0 ? (
          <Popover
            title={
              <div className="flex items-center gap-[8px] p-[16px] border-b border-kds-border">
                <span className="text-[18px] font-semibold text-kds-fg">
                  <Trans>Tasks</Trans>
                </span>
                {processingTasks.length > 0 && (
                  <Badge
                    count={processingTasks.length}
                    style={{ backgroundColor: "var(--kds-danger)" }}
                    className="text-[12px]"
                  />
                )}
              </div>
            }
            align={{ offset: [20, 0] }}
            content={libraryPopoverContent}
            trigger="hover"
            placement="right"
            onOpenChange={(open) => open && resetPolling()}
          >
            {libraryContent}
          </Popover>
        ) : (
          libraryContent
        )}

        <div
          className={classNames(
            "group/credit flex items-center justify-between cursor-pointer",
            isOpen ? "p-[8px] gap-[4px]" : "flex-col",
          )}
          onClick={() => {
            report.event("kalo_click", { kalo_type: "ai_credits", kalo_value: "kaloclip" });
            addGasStationToShoppingCart(CreditConfig);
            pushGtmEvent(GtmEvent.GoToViewCredits);
          }}
        >
          <IconCredit />
          <div className="text-kds-fg font-bold whitespace-nowrap leading-[20px]">
            {totalRemain >= 10000 ? "9999+" : totalRemain.toFixed(1)}
          </div>
          <div className="flex-1"></div>
          {isOpen && (
            <div className="invisible group-hover/credit:visible">
              <CommonButton size="small">
                <Trans>Top up</Trans>
              </CommonButton>
            </div>
          )}
        </div>
      </div>
      <div className="p-[6px] border-t border-kds-border">
        <AccountDrawer isOpen={isOpen} />
      </div>
    </div>
  );
};
